// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/Reports/ReportsPage.tsx

import { useEffect, useMemo, useRef, useState } from "react";
import "./ReportsPage.css";
import {
  EXPORT_INCOMPLETE_MESSAGE,
  REPORTS_EXPORT_CHUNK_SIZE,
  REPORTS_PAGE_SIZE,
  buildReportsUrl,
  collectReportExportRows,
  getReportsOffset,
  getReportsTotalPages,
} from "./reportsExport";
import { portalFetch } from "../../../../utils/portalRoleChoice";

type DateRangePreset =
  | "last30"
  | "last3months"
  | "month"
  | "year"
  | "custom";

type ReportFilters = {
  dateRangePreset: DateRangePreset;
  month: string;
  year: string;
  customStartDate: string;
  customEndDate: string;
  sourceType: string;
  status: string;
  outcome: string;
};

type ReportRow = {
  navigatorCaseId: number;
  patientName: string | null;
  fsa: string | null;
  city: string | null;
  sourceType: string | null;
  sourceLabel: string | null;
  status: string | null;
  statusCode?: string | null;
  createdAt: string | null;
  attachmentCompletedAt: string | null;
  outcome: string | null;
  outcomeCode?: string | null;
  timeToAttachment: string | null;
  timeToAttachmentDays?: number | null;
};

type ReportsApiUser = {
  userId?: number;
  role?: string;
  ohtId?: number;
  ohtName?: string | null;
  ohtCode?: string | null;
};

type ReportsApiResponse = {
  ok: boolean;
  error?: string;
  user?: ReportsApiUser;
  items?: ReportRow[];
  paging?: {
    total?: number;
    limit?: number;
    offset?: number;
  };
};

export const sourceOptions = [
  { value: "all", label: "All sources" },
  { value: "Lists", label: "Lists" },
  { value: "Public", label: "Public" },
  { value: "Manual", label: "Manual" },
];

export function formatReportSourceType(
  sourceType: string | null | undefined
): string {
  switch (sourceType) {
    case "NavigatorUpload":
    case "OHT":
      return "Lists";
    case "NavigatorPublicRequest":
      return "Public";
    case "ManualNavigatorEntry":
      return "Manual";
    case "FutureHCC":
      return "Future HCC";
    case "Other":
      return "Other";
    default:
      return sourceType || "—";
  }
}

export function formatReportSourceDisplay(
  sourceType: string | null | undefined,
  sourceLabel: string | null | undefined
): string {
  const category = formatReportSourceType(sourceType);
  if (sourceLabel) return `${category} · ${sourceLabel}`;
  return category;
}

const statusOptions = [
  { value: "all", label: "All statuses" },
  { value: "New", label: "New" },
  { value: "Assigned", label: "Assigned" },
  { value: "IntakeIncomplete", label: "Intake incomplete" },
  { value: "IntakeComplete", label: "Intake complete" },
  { value: "Matched", label: "Matched" },
  { value: "Forwarded", label: "Forwarded" },
  { value: "SubmittedToDoctor", label: "Submitted to doctor" },
  { value: "Attached", label: "Attached" },
  { value: "Closed", label: "Closed" },
];

const outcomeOptions = [
  { value: "all", label: "All outcomes" },
  { value: "Attached", label: "Attached" },
  { value: "WaitingForClinic", label: "Waiting for clinic" },
  { value: "Waitlisted", label: "Waitlisted" },
  { value: "Declined", label: "Declined" },
  { value: "NoSuitableMatch", label: "No suitable match" },
  { value: "PatientUnreachable", label: "Patient unreachable" },
  { value: "PatientDeclined", label: "Patient declined" },
  { value: "AcceptedElsewhere", label: "Accepted elsewhere" },
  { value: "ClosedAdministrative", label: "Closed administratively" },
];

const initialFilters: ReportFilters = {
  dateRangePreset: "last30",
  month: "",
  year: String(new Date().getFullYear()),
  customStartDate: "",
  customEndDate: "",
  sourceType: "all",
  status: "all",
  outcome: "all",
};

function getPresetLabel(value: DateRangePreset): string {
  if (value === "last30") return "Last 30 days";
  if (value === "last3months") return "Last 3 months";
  if (value === "month") return "Month";
  if (value === "year") return "Year";
  return "Custom date range";
}

function findOptionLabel(
  options: { value: string; label: string }[],
  value: string | null | undefined
): string {
  if (!value) return "—";
  return options.find((option) => option.value === value)?.label || value;
}

function buildFilterSummary(filters: ReportFilters): string {
  const parts = [
    `Date range: ${getPresetLabel(filters.dateRangePreset)}`,
    `Source: ${findOptionLabel(sourceOptions, filters.sourceType)}`,
    `Status: ${findOptionLabel(statusOptions, filters.status)}`,
    `Outcome: ${findOptionLabel(outcomeOptions, filters.outcome)}`,
  ];

  if (filters.dateRangePreset === "month" && filters.month) {
    parts.push(`Month selected: ${filters.month}`);
  }

  if (filters.dateRangePreset === "year" && filters.year) {
    parts.push(`Year selected: ${filters.year}`);
  }

  if (
    filters.dateRangePreset === "custom" &&
    (filters.customStartDate || filters.customEndDate)
  ) {
    parts.push(
      `Custom range: ${filters.customStartDate || "Start"} to ${
        filters.customEndDate || "End"
      }`
    );
  }

  return parts.join(" · ");
}

function formatArea(row: ReportRow): string {
  if (row.fsa && row.city) return `${row.fsa} / ${row.city}`;
  if (row.fsa) return row.fsa;
  if (row.city) return row.city;
  return "—";
}

function formatDateTime(value: Date | string | null): string {
  if (!value) return "—";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function calculateDayDifference(
  startValue: string | null,
  endValue: string | null
): number | null {
  if (!startValue || !endValue) return null;

  const startDate = new Date(startValue);
  const endDate = new Date(endValue);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return null;
  }

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / millisecondsPerDay
  );

  return Math.max(0, diff);
}

function getTimeToAttachmentDisplay(row: ReportRow): string {
  if (row.timeToAttachment) return row.timeToAttachment;

  const calculatedDays = calculateDayDifference(
    row.createdAt,
    row.attachmentCompletedAt
  );

  if (calculatedDays !== null) {
    return `${calculatedDays} day${calculatedDays === 1 ? "" : "s"}`;
  }

  if (String(row.statusCode || row.status || "").toLowerCase() === "closed") {
    return "Not attached";
  }

  return "Not attached yet";
}

function escapeHtml(value: unknown): string {
  return String(value ?? "—")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function buildPrintableReportHtml({
  reportRows,
  reportUser,
  totalRows,
  filterSummary,
  generatedAt,
}: {
  reportRows: ReportRow[];
  reportUser: ReportsApiUser | null;
  totalRows: number;
  filterSummary: string;
  generatedAt: Date;
}): string {
  const ohtName = reportUser?.ohtName || "Assigned OHT";
  const ohtCode = reportUser?.ohtCode || "—";

  const rowsHtml =
    reportRows.length > 0
      ? reportRows
          .map((row) => {
            const source = formatReportSourceDisplay(
              row.sourceType,
              row.sourceLabel
            );

            return `
              <tr>
                <td>${escapeHtml(`#${row.navigatorCaseId}`)}</td>
                <td>${escapeHtml(row.patientName || "—")}</td>
                <td>${escapeHtml(formatArea(row))}</td>
                <td>${escapeHtml(source)}</td>
                <td>${escapeHtml(row.status || "—")}</td>
                <td>${escapeHtml(row.outcome || "—")}</td>
                <td>${escapeHtml(getTimeToAttachmentDisplay(row))}</td>
              </tr>
            `;
          })
          .join("")
      : `
        <tr>
          <td colspan="7" class="empty">No report data found for the selected filters.</td>
        </tr>
      `;

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Community Navigator Report</title>
  <style>
    @page {
      size: A4 landscape;
      margin: 12mm;
    }

    * {
      box-sizing: border-box;
    }

    html,
    body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #172033;
      font-family: Arial, Helvetica, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .report {
      width: 100%;
    }

    .header {
      border: 1px solid #cbd5e1;
      padding: 12px 14px;
      margin-bottom: 12px;
      display: grid;
      grid-template-columns: 1fr 1.7fr;
      gap: 16px;
    }

    .eyebrow {
      margin: 0 0 5px;
      color: #1454a8;
      font-size: 10px;
      font-weight: 800;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h1 {
      margin: 0;
      font-size: 22px;
      line-height: 1.1;
      color: #172033;
    }

    .oht-code {
      display: inline-block;
      margin-top: 8px;
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      padding: 4px 8px;
      border-radius: 999px;
      color: #172033;
      font-size: 10px;
      font-weight: 700;
    }

    .meta {
      display: grid;
      gap: 6px;
      margin: 0;
    }

    .meta-row {
      display: grid;
      grid-template-columns: 95px 1fr;
      gap: 8px;
      border: 1px solid #e2e8f0;
      padding: 6px 8px;
    }

    .meta-row dt {
      margin: 0;
      color: #475569;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .meta-row dd {
      margin: 0;
      color: #172033;
      font-size: 9.5px;
      line-height: 1.25;
      font-weight: 600;
    }

    .table-title {
      margin: 0 0 8px;
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 12px;
    }

    .table-title h2 {
      margin: 0;
      color: #172033;
      font-size: 15px;
    }

    .table-title span {
      border: 1px solid #cbd5e1;
      background: #f8fafc;
      padding: 4px 8px;
      border-radius: 999px;
      font-size: 9px;
      font-weight: 800;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      border: 1px solid #cbd5e1;
    }

    th,
    td {
      border-bottom: 1px solid #e2e8f0;
      border-right: 1px solid #e2e8f0;
      padding: 6px 7px;
      text-align: left;
      vertical-align: top;
      overflow-wrap: anywhere;
      font-size: 9px;
      line-height: 1.25;
    }

    th {
      background: #f1f5fb;
      color: #172033;
      font-size: 8px;
      font-weight: 900;
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }

    th:last-child,
    td:last-child {
      border-right: 0;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    th:nth-child(1),
    td:nth-child(1) {
      width: 8%;
      white-space: nowrap;
    }

    th:nth-child(2),
    td:nth-child(2) {
      width: 18%;
    }

    th:nth-child(3),
    td:nth-child(3) {
      width: 12%;
    }

    th:nth-child(4),
    td:nth-child(4) {
      width: 20%;
    }

    th:nth-child(5),
    td:nth-child(5) {
      width: 13%;
    }

    th:nth-child(6),
    td:nth-child(6) {
      width: 14%;
    }

    th:nth-child(7),
    td:nth-child(7) {
      width: 15%;
    }

    .empty {
      text-align: center;
      padding: 24px;
      color: #64748b;
      font-weight: 700;
    }
  </style>
</head>
<body>
  <main class="report">
    <section class="header">
      <div>
        <p class="eyebrow">Community Navigator Report</p>
        <h1>${escapeHtml(ohtName)}</h1>
        <span class="oht-code">${escapeHtml(ohtCode)}</span>
      </div>

      <dl class="meta">
        <div class="meta-row">
          <dt>Generated</dt>
          <dd>${escapeHtml(formatDateTime(generatedAt))}</dd>
        </div>
        <div class="meta-row">
          <dt>Rows</dt>
          <dd>${escapeHtml(totalRows)}</dd>
        </div>
        <div class="meta-row">
          <dt>Filters</dt>
          <dd>${escapeHtml(filterSummary)}</dd>
        </div>
      </dl>
    </section>

    <section class="table-title">
      <h2>Report table</h2>
      <span>${escapeHtml(totalRows)} rows</span>
    </section>

    <table>
      <thead>
        <tr>
          <th>Case</th>
          <th>Patient</th>
          <th>Area</th>
          <th>Source</th>
          <th>Status</th>
          <th>Outcome</th>
          <th>Time to attachment</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  </main>
</body>
</html>`;
}

export default function ReportsPage() {
  const [filters, setFilters] = useState<ReportFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<ReportFilters>(initialFilters);

  const [reportRows, setReportRows] = useState<ReportRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [reportUser, setReportUser] = useState<ReportsApiUser | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const [exportRequestedAt, setExportRequestedAt] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const isExportingRef = useRef(false);
  const exportWindowRef = useRef<Window | null>(null);

  const filterSummary = useMemo(
    () => buildFilterSummary(appliedFilters),
    [appliedFilters]
  );

  const generatedAt = exportRequestedAt || lastLoadedAt;

  useEffect(() => {
    const controller = new AbortController();

    async function loadReports() {
      setIsLoadingReports(true);
      setReportsError(null);

      try {
        const response = await portalFetch(
          buildReportsUrl(appliedFilters, {
            limit: REPORTS_PAGE_SIZE,
            offset: getReportsOffset(page, REPORTS_PAGE_SIZE),
          }),
          {
            method: "GET",
            credentials: "include",
            signal: controller.signal,
          }
        );

        const payload = (await response.json()) as ReportsApiResponse;

        if (!response.ok || !payload.ok) {
          throw new Error(payload?.error || "Unable to load navigator reports.");
        }

        const total = Number(payload.paging?.total || 0);
        const nextTotalPages = getReportsTotalPages(total, REPORTS_PAGE_SIZE);

        if (page > nextTotalPages) {
          setTotalRows(total);
          setPage(nextTotalPages);
          return;
        }

        setReportRows(Array.isArray(payload.items) ? payload.items : []);
        setTotalRows(total);
        setReportUser(payload.user || null);
        setLastLoadedAt(new Date());
        if (!isExportingRef.current) {
          setExportRequestedAt(null);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }

        setReportRows([]);
        setTotalRows(0);
        if (page !== 1) {
          setPage(1);
        }
        setReportsError(
          err instanceof Error
            ? err.message
            : "Unable to load navigator reports."
        );
      } finally {
        setIsLoadingReports(false);
      }
    }

    loadReports();

    return () => {
      controller.abort();
    };
  }, [appliedFilters, page]);

  useEffect(() => {
    return () => {
      if (!isExportingRef.current) return;
      const reserved = exportWindowRef.current;
      if (reserved && !reserved.closed) {
        reserved.close();
      }
    };
  }, []);

  function updateFilter<K extends keyof ReportFilters>(
    key: K,
    value: ReportFilters[K]
  ) {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function handleApplyFilters() {
    setAppliedFilters(filters);
    setPage(1);
  }

  function handleResetFilters() {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  }

  function handlePageChange(nextPage: number) {
    const totalPages = getReportsTotalPages(totalRows, REPORTS_PAGE_SIZE);
    const safePage = Math.max(1, Math.min(totalPages, nextPage));
    setPage(safePage);
  }

  async function handleExportPdf() {
    if (isExportingRef.current) return;

    const printWindow = window.open("", "_blank", "width=1200,height=800");

    if (!printWindow) {
      isExportingRef.current = false;
      setIsExporting(false);
      setReportsError(EXPORT_INCOMPLETE_MESSAGE);
      return;
    }

    exportWindowRef.current = printWindow;
    printWindow.document.open();
    printWindow.document.write(
      "<!doctype html><html><head><title>Preparing report…</title></head><body><p>Preparing report…</p></body></html>"
    );
    printWindow.document.close();

    const snapshot = appliedFilters;
    const generated = new Date();
    isExportingRef.current = true;
    setIsExporting(true);
    setExportRequestedAt(generated);
    setReportsError(null);

    const abandonReservedWindow = () => {
      const reserved = exportWindowRef.current;
      exportWindowRef.current = null;
      if (reserved && !reserved.closed) {
        reserved.close();
      }
    };

    try {
      const result = await collectReportExportRows<ReportRow>({
        chunkSize: REPORTS_EXPORT_CHUNK_SIZE,
        fetchChunk: async (offset, limit) => {
          const response = await portalFetch(
            buildReportsUrl(snapshot, { limit, offset }),
            {
              method: "GET",
              credentials: "include",
            }
          );
          const payload = (await response.json()) as ReportsApiResponse;

          if (!response.ok || !payload.ok) {
            throw new Error(payload?.error || EXPORT_INCOMPLETE_MESSAGE);
          }

          return {
            items: Array.isArray(payload.items) ? payload.items : [],
            total: Number(payload.paging?.total),
          };
        },
      });

      if (!result.ok) {
        abandonReservedWindow();
        setReportsError(result.error);
        return;
      }

      if (printWindow.closed) {
        exportWindowRef.current = null;
        setReportsError(EXPORT_INCOMPLETE_MESSAGE);
        return;
      }

      const auditResponse = await portalFetch(
        "/api/portal/community-navigator/report-export-audit",
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        }
      );
      const auditPayload = (await auditResponse.json().catch(() => null)) as {
        ok?: boolean;
      } | null;

      if (!auditResponse.ok || !auditPayload?.ok) {
        abandonReservedWindow();
        setReportsError(EXPORT_INCOMPLETE_MESSAGE);
        return;
      }

      if (printWindow.closed) {
        exportWindowRef.current = null;
        setReportsError(EXPORT_INCOMPLETE_MESSAGE);
        return;
      }

      const html = buildPrintableReportHtml({
        reportRows: result.rows,
        reportUser,
        totalRows: result.rows.length,
        filterSummary: buildFilterSummary(snapshot),
        generatedAt: generated,
      });

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();

      if (printWindow.closed) {
        exportWindowRef.current = null;
        setReportsError(EXPORT_INCOMPLETE_MESSAGE);
        return;
      }

      window.setTimeout(() => {
        if (!printWindow.closed) {
          printWindow.print();
        }
      }, 250);
    } catch {
      abandonReservedWindow();
      setReportsError(EXPORT_INCOMPLETE_MESSAGE);
    } finally {
      isExportingRef.current = false;
      setIsExporting(false);
    }
  }

  return (
    <div className="cn-reports-page">
      <section className="cn-reports-hero">
        <div>
          <p className="cn-reports-eyebrow">Community Navigator</p>
          <h1>Navigator Reports</h1>
          <p>
            Review navigator case sources, status, outcomes, and time to
            attachment using a filtered report table.
          </p>
        </div>

        <button
          type="button"
          className="cn-reports-export-button"
          onClick={handleExportPdf}
          disabled={isLoadingReports || isExporting}
        >
          {isExporting ? "Exporting…" : "Export PDF"}
        </button>
      </section>

      <section className="cn-reports-print-heading">
        <div>
          <p>Community Navigator Report</p>
          <h2>{reportUser?.ohtName || "Assigned OHT"}</h2>
          {reportUser?.ohtCode ? <span>{reportUser.ohtCode}</span> : null}
        </div>

        <dl>
          <div>
            <dt>Generated</dt>
            <dd>{formatDateTime(generatedAt)}</dd>
          </div>
          <div>
            <dt>Rows</dt>
            <dd>{totalRows}</dd>
          </div>
          <div>
            <dt>Filters</dt>
            <dd>{filterSummary}</dd>
          </div>
        </dl>
      </section>

      <section className="cn-reports-filter-card">
        <div className="cn-reports-filter-header">
          <div>
            <h2>Report filters</h2>
            <p>
              Select a time period and reporting scope, then apply filters to
              refresh the table.
            </p>
          </div>
        </div>

        <div className="cn-reports-filter-grid">
          <label>
            <span>Date range</span>
            <select
              value={filters.dateRangePreset}
              onChange={(event) =>
                updateFilter(
                  "dateRangePreset",
                  event.target.value as DateRangePreset
                )
              }
            >
              <option value="last30">Last 30 days</option>
              <option value="last3months">Last 3 months</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
              <option value="custom">Custom date range</option>
            </select>
          </label>

          {filters.dateRangePreset === "month" ? (
            <label>
              <span>Month</span>
              <input
                type="month"
                value={filters.month}
                onChange={(event) => updateFilter("month", event.target.value)}
              />
            </label>
          ) : null}

          {filters.dateRangePreset === "year" ? (
            <label>
              <span>Year</span>
              <input
                type="number"
                min="2020"
                max="2100"
                value={filters.year}
                onChange={(event) => updateFilter("year", event.target.value)}
              />
            </label>
          ) : null}

          {filters.dateRangePreset === "custom" ? (
            <>
              <label>
                <span>Start date</span>
                <input
                  type="date"
                  value={filters.customStartDate}
                  onChange={(event) =>
                    updateFilter("customStartDate", event.target.value)
                  }
                />
              </label>

              <label>
                <span>End date</span>
                <input
                  type="date"
                  value={filters.customEndDate}
                  onChange={(event) =>
                    updateFilter("customEndDate", event.target.value)
                  }
                />
              </label>
            </>
          ) : null}

          <label>
            <span>Source</span>
            <select
              value={filters.sourceType}
              onChange={(event) =>
                updateFilter("sourceType", event.target.value)
              }
            >
              {sourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Status</span>
            <select
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Outcome</span>
            <select
              value={filters.outcome}
              onChange={(event) => updateFilter("outcome", event.target.value)}
            >
              {outcomeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="cn-reports-filter-actions">
          <button
            type="button"
            onClick={handleResetFilters}
            disabled={isLoadingReports}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApplyFilters}
            disabled={isLoadingReports}
          >
            {isLoadingReports ? "Loading..." : "Apply filters"}
          </button>
        </div>
      </section>

      <section className="cn-reports-table-card">
        <div className="cn-reports-table-header">
          <div>
            <h2 id="cn-reports-table-heading">Report table</h2>
            <p>{filterSummary}</p>
          </div>

          <span>{totalRows} rows</span>
        </div>

        {reportsError ? (
          <div className="cn-reports-error" role="alert">
            {reportsError}
          </div>
        ) : null}

        <div
          className="cn-reports-table-scroll"
          tabIndex={0}
          role="region"
          aria-labelledby="cn-reports-table-heading"
        >
          <table className="cn-reports-table">
            <thead>
              <tr>
                <th>Case</th>
                <th>Patient</th>
                <th>Area</th>
                <th>Source</th>
                <th>Status</th>
                <th>Outcome</th>
                <th>Time to attachment</th>
              </tr>
            </thead>

            <tbody>
              {reportRows.length > 0 ? (
                reportRows.map((row) => (
                  <tr key={row.navigatorCaseId}>
                    <td>#{row.navigatorCaseId}</td>
                    <td>{row.patientName || "—"}</td>
                    <td>{formatArea(row)}</td>
                    <td>
                      {formatReportSourceDisplay(row.sourceType, row.sourceLabel)}
                    </td>
                    <td>{row.status || "—"}</td>
                    <td>{row.outcome || "—"}</td>
                    <td>{getTimeToAttachmentDisplay(row)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="cn-reports-empty-state">
                      <h3>
                        {isLoadingReports
                          ? "Loading report data..."
                          : "No report data found"}
                      </h3>
                      <p>
                        {isLoadingReports
                          ? "The report table will update shortly."
                          : "Try changing the date range, source, status, or outcome filters."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {getReportsTotalPages(totalRows, REPORTS_PAGE_SIZE) > 1 ? (
          <div className="cn-reports-pagination" aria-label="Reports pagination">
            <button
              type="button"
              className="cn-reports-pagination-button"
              onClick={() => handlePageChange(page - 1)}
              disabled={isLoadingReports || page <= 1}
            >
              Previous
            </button>
            <span className="cn-reports-pagination-status">
              Page {page} of {getReportsTotalPages(totalRows, REPORTS_PAGE_SIZE)}
            </span>
            <button
              type="button"
              className="cn-reports-pagination-button"
              onClick={() => handlePageChange(page + 1)}
              disabled={
                isLoadingReports ||
                page >= getReportsTotalPages(totalRows, REPORTS_PAGE_SIZE)
              }
            >
              Next
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}